import type { Id, Ms } from '@rota/contracts';
import { ArrowRight, ChevronLeft, Eye, EyeOff, TriangleAlert } from 'lucide-react';
import { type FormEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import { cx } from '@/lib/cx';
import { suiteCopy } from './copy';
import styles from './LoginCard.module.css';

export interface RememberedAccount {
  id: Id;
  name: string;
  initials: string;
  email: string;
}

export interface LoginCardProps {
  /** Contas lembradas neste aparelho ("Manter conectado"); vazio mostra direto e-mail e senha. */
  remembered: RememberedAccount[];
  onSubmit(i: { email: string; password: string; remember: boolean }): Promise<void>;
  /** Sempre genérica: "E-mail ou senha incorretos." */
  error?: string;
  /** Muda a cada tentativa recusada: repete a tremida e o foco mesmo com o mesmo texto. */
  errorNonce?: number;
  /** Espera progressiva: até quando o botão fica bloqueado. */
  retryAt?: Ms;
  onForgot?(): void;
  /** O campo de senha recebeu foco (o app aproveita para baixar o que vem depois do login). */
  onPasswordFocus?(): void;
  className?: string;
}

function mmss(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Cartão de login (SPEC-v2 2.2), inspirado na janela de login do macOS: contas lembradas com avatar
 * grande; senão, e-mail e senha num grupo inset. Mostrar senha, aviso de Caps Lock, erro genérico
 * com tremida (desligada com "reduzir movimento") e o foco de volta na senha; espera progressiva
 * com contagem no botão.
 */
export function LoginCard({
  remembered,
  onSubmit,
  error,
  errorNonce = 0,
  retryAt,
  onForgot,
  onPasswordFocus,
  className,
}: LoginCardProps) {
  const c = suiteCopy.login;
  const id = useId();
  const [chosen, setChosen] = useState<RememberedAccount | null>(null);
  const [other, setOther] = useState(remembered.length === 0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(false);
  const [caps, setCaps] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [shake, setShake] = useState(0);
  const pwd = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const errId = `${id}-err`;
  const capsId = `${id}-caps`;
  const locked = retryAt != null && retryAt > now;

  useEffect(() => {
    if (!locked) return;
    const t = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(t);
  }, [locked]);

  useEffect(() => {
    if (retryAt) setNow(Date.now());
  }, [retryAt]);

  // Erro novo: tremida e foco de volta na senha (com o texto selecionado para redigitar).
  // biome-ignore lint/correctness/useExhaustiveDependencies: o nonce marca cada tentativa recusada
  useEffect(() => {
    if (!error) return;
    setShake((n) => n + 1);
    pwd.current?.focus();
    pwd.current?.select();
  }, [error, errorNonce]);

  const picking = remembered.length > 0 && !chosen && !other;

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (busy || locked) return;
    const mail = chosen?.email ?? email;
    if (!mail || !password) {
      (mail ? pwd : emailRef).current?.focus();
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ email: mail, password, remember: chosen ? true : remember });
    } finally {
      setBusy(false);
    }
  }

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    setCaps(typeof e.getModifierState === 'function' && e.getModifierState('CapsLock'));
  };

  const describedBy = [error ? errId : null, caps ? capsId : null].filter(Boolean).join(' ') || undefined;

  const passwordField = (inlineSubmit: boolean) => (
    <div className={styles.row} data-last>
      <label htmlFor={`${id}-pwd`} className={styles.srOnly}>
        {c.password}
      </label>
      <input
        ref={pwd}
        id={`${id}-pwd`}
        className={styles.input}
        type={show ? 'text' : 'password'}
        name="password"
        autoComplete="current-password"
        placeholder={c.password}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onKeyDown={onKey}
        onKeyUp={onKey}
        onFocus={onPasswordFocus}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        data-testid="login-password"
        // biome-ignore lint/a11y/noAutofocus: escolher a conta lembrada leva direto à senha, como no macOS
        autoFocus={inlineSubmit}
      />
      <button
        type="button"
        className={styles.iconButton}
        onClick={() => setShow((s) => !s)}
        aria-label={show ? c.hide : c.show}
        aria-pressed={show}
      >
        {show ? (
          <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" />
        ) : (
          <Eye size={18} strokeWidth={1.75} aria-hidden="true" />
        )}
      </button>
      {inlineSubmit && (
        <button
          type="submit"
          className={styles.arrow}
          aria-label={locked ? c.retryIn(mmss((retryAt ?? 0) - now)) : c.submit}
          disabled={busy || locked}
        >
          {busy ? (
            <Spinner size={16} decorative />
          ) : (
            <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
          )}
        </button>
      )}
    </div>
  );

  return (
    <form
      className={cx(styles.card, className)}
      onSubmit={submit}
      noValidate
      data-shake={shake % 2 === 1 ? 'a' : shake > 0 ? 'b' : undefined}
      aria-busy={busy || undefined}
    >
      {picking ? (
        <div className={styles.accounts}>
          <ul className={styles.accountList}>
            {remembered.map((a) => (
              <li key={a.id}>
                <button type="button" className={styles.account} onClick={() => setChosen(a)}>
                  <span className={styles.bigAvatar} aria-hidden="true">
                    {a.initials}
                  </span>
                  <span className={styles.accountName}>{a.name}</span>
                  <span className={styles.srOnly}>{c.rememberedAs(a.name)}</span>
                </button>
              </li>
            ))}
          </ul>
          <Button variant="plain" size="sm" onPress={() => setOther(true)}>
            {c.otherAccount}
          </Button>
        </div>
      ) : chosen ? (
        <div className={styles.chosen}>
          <span className={styles.bigAvatar} aria-hidden="true">
            {chosen.initials}
          </span>
          <p className={styles.chosenName}>{chosen.name}</p>
          <div className={styles.group}>{passwordField(true)}</div>
          <button
            type="button"
            className={styles.link}
            onClick={() => {
              setChosen(null);
              setPassword('');
            }}
          >
            <ChevronLeft size={14} strokeWidth={2} aria-hidden="true" />
            {c.otherAccount}
          </button>
        </div>
      ) : (
        <>
          <div className={styles.group}>
            <div className={styles.row}>
              <label htmlFor={`${id}-email`} className={styles.srOnly}>
                {c.email}
              </label>
              <input
                ref={emailRef}
                id={`${id}-email`}
                className={styles.input}
                type="email"
                name="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder={c.email}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errId : undefined}
                data-testid="login-email"
              />
            </div>
            {passwordField(false)}
          </div>
          <label className={styles.check}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span>{c.remember}</span>
          </label>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={busy}
            disabled={locked}
            data-testid="login-submit"
          >
            {locked ? c.retryIn(mmss((retryAt ?? 0) - now)) : busy ? c.submitting : c.submit}
          </Button>
        </>
      )}

      <div className={styles.messages} data-empty={!caps && !error ? true : undefined}>
        {caps && (
          <p id={capsId} className={styles.caps}>
            <TriangleAlert size={14} strokeWidth={1.75} aria-hidden="true" />
            {c.capsLock}
          </p>
        )}
        <p id={errId} className={styles.error} role="alert" aria-live="assertive">
          {error ?? ''}
        </p>
      </div>

      {onForgot && !picking && (
        <button type="button" className={styles.forgot} onClick={onForgot}>
          {c.forgot}
        </button>
      )}
    </form>
  );
}
